import React, { Component } from 'react';
import { array, arrayOf, bool, func, number, shape, string, oneOf } from 'prop-types';
import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import config from '../../config';
import routeConfiguration from '../../routeConfiguration';
import { localizeFilters } from '../../util/filtersLocalizationHelpers';
import { findOptionsForSelectFilter } from '../../util/search';
import { getSelectedSpaceTypeKey, SPACE_TYPE_ALL_KEY } from '../../util/spaceTypes';
import { LISTING_STATE_PENDING_APPROVAL, LISTING_STATE_CLOSED, propTypes } from '../../util/types';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
  createSlug,
} from '../../util/urlHelpers';
import { formatMoney } from '../../util/currency';
import { createResourceLocatorString, findRouteByRouteName } from '../../util/routes';
import {
  ensureListing,
  ensureOwnListing,
  ensureUser,
  userDisplayNameAsString,
} from '../../util/data';
import { getListingCustomerDisplayPrice } from '../../util/pricing';
import { publicListingMapLocation } from '../../util/api';
import { richText } from '../../util/richText';
import { withViewport } from '../../util/contextHelpers';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/UI.duck';
import { initializeCardPaymentData } from '../../ducks/stripe.duck.js';
import {
  Page,
  NamedLink,
  NamedRedirect,
  LayoutSingleColumn,
  LayoutWrapperTopbar,
  LayoutWrapperMain,
  LayoutWrapperFooter,
  Footer,
  BookingPanel,
} from '../../components';
import facebookImage from '../../assets/uupo-share-facebook-1200x630.png';
import { TopbarContainer, NotFoundPage } from '../../containers';

import { sendEnquiry, fetchTransactionLineItems, setInitialValues } from './ListingPage.duck';
import SectionImages from './SectionImages';
import SectionHeading from './SectionHeading';
import SectionDescriptionMaybe from './SectionDescriptionMaybe';
import SectionDenominacionMaybe from './SectionDenominacionMaybe';
import SectionOrientacionMaybe from './SectionOrientacionMaybe';
import SectionAlturaMaybe from './SectionAlturaMaybe';
import SectionFeaturesMaybe from './SectionFeaturesMaybe';
import SectionActividadComercialMaybe from './SectionActividadComercialMaybe';
import SectionAmenitiesUupoMaybe from './SectionAmenitiesUupoMaybe';
import SectionAmenitiesPopMaybe from './SectionAmenitiesPopMaybe';
import SectionCategoriaMaybe from './SectionCategoriaMaybe';
import SectionMedidasMaybe from './SectionMedidasMaybe';
import SectionReviews from './SectionReviews';
import SectionHostMaybe from './SectionHostMaybe';
import SectionMapMaybe from './SectionMapMaybe';
import img1 from '../../assets/landingUppo/UUseguro.png';
import css from './ListingPage.module.css';

const MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE = 16;
const DESKTOP_BOOKING_PANEL_MIN_WIDTH = 1024;

const { UUID, LatLng } = sdkTypes;

const priceData = (price, intl) => {
  if (price && price.currency === config.currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: `(${price.currency})`,
      priceTitle: `Unsupported currency (${price.currency})`,
    };
  }
  return {};
};

const categoryLabel = (categories, key) => {
  const cat = categories.find(c => c.key === key);
  return cat ? cat.label : key;
};

export class ListingPageComponent extends Component {
  constructor(props) {
    super(props);
    const { enquiryModalOpenForListingId, params } = props;
    this.state = {
      pageClassNames: [],
      imageCarouselOpen: false,
      enquiryModalOpen: enquiryModalOpenForListingId === params.id,
      publicMapGeolocation: null,
      publicMapLocationApproximate: false,
      publicMapLocationLoadedFor: null,
    };

    this.handleSubmit = this.handleSubmit.bind(this);
    this.onContactUser = this.onContactUser.bind(this);
    this.onSubmitEnquiry = this.onSubmitEnquiry.bind(this);
    this.loadPublicMapLocationIfNeeded = this.loadPublicMapLocationIfNeeded.bind(this);
  }

  componentDidMount() {
    this.loadPublicMapLocationIfNeeded(this.props);
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.params.id !== this.props.params.id ||
      prevProps.params.variant !== this.props.params.variant ||
      prevProps.getListing !== this.props.getListing ||
      prevProps.getOwnListing !== this.props.getOwnListing
    ) {
      this.loadPublicMapLocationIfNeeded(this.props);
    }
  }

  loadPublicMapLocationIfNeeded(props) {
    const { getListing, getOwnListing, params } = props;
    const listingId = new UUID(params.id);
    const isPendingApprovalVariant = params.variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT;
    const isDraftVariant = params.variant === LISTING_PAGE_DRAFT_VARIANT;
    const currentListing =
      isPendingApprovalVariant || isDraftVariant
        ? ensureOwnListing(getOwnListing(listingId))
        : ensureListing(getListing(listingId));
    const approximateGeolocation = currentListing.attributes?.publicData?.approximateGeolocation;
    const isPublicListingPage = !isPendingApprovalVariant && !isDraftVariant;

    if (!isPublicListingPage || approximateGeolocation) {
      if (this.state.publicMapLocationLoadedFor !== null || this.state.publicMapGeolocation) {
        this.setState({
          publicMapGeolocation: null,
          publicMapLocationApproximate: false,
          publicMapLocationLoadedFor: null,
        });
      }
      return;
    }

    if (!currentListing.id || this.state.publicMapLocationLoadedFor === listingId.uuid) {
      if (
        this.state.publicMapLocationLoadedFor &&
        this.state.publicMapLocationLoadedFor !== listingId.uuid &&
        this.state.publicMapGeolocation
      ) {
        this.setState({
          publicMapGeolocation: null,
          publicMapLocationApproximate: false,
          publicMapLocationLoadedFor: null,
        });
      }
      return;
    }

    publicListingMapLocation({ listingId })
      .then(response => {
        const { geolocation, locationApproximate } = response.data || {};

        this.setState({
          publicMapGeolocation: geolocation || null,
          publicMapLocationApproximate: !!locationApproximate,
          publicMapLocationLoadedFor: listingId.uuid,
        });
      })
      .catch(() => {
        this.setState({
          publicMapGeolocation: null,
          publicMapLocationApproximate: false,
          publicMapLocationLoadedFor: listingId.uuid,
        });
      });
  }

  handleSubmit(values) {
    const {
      history,
      getListing,
      params,
      callSetInitialValues,
      onInitializeCardPaymentData,
    } = this.props;
    const listingId = new UUID(params.id);
    const listing = getListing(listingId);

    const { bookingDates, ...bookingData } = values;

    const initialValues = {
      listing,
      bookingData,
      bookingDates: {
        bookingStart: bookingDates.startDate,
        bookingEnd: bookingDates.endDate,
      },
      confirmPaymentError: null,
    };

    const saveToSessionStorage = !this.props.currentUser;

    const routes = routeConfiguration();
    // Customize checkout page state with current listing and selected bookingDates
    const { setInitialValues } = findRouteByRouteName('CheckoutPage', routes);

    callSetInitialValues(setInitialValues, initialValues, saveToSessionStorage);

    // Clear previous Stripe errors from store if there is any
    onInitializeCardPaymentData();

    // Redirect to CheckoutPage
    history.push(
      createResourceLocatorString(
        'CheckoutPage',
        routes,
        { id: listing.id.uuid, slug: createSlug(listing.attributes.title) },
        {}
      )
    );
  }

  onContactUser() {
    const { currentUser, history, callSetInitialValues, params, location } = this.props;

    if (!currentUser) {
      const state = { from: `${location.pathname}${location.search}${location.hash}` };

      // We need to log in before showing the modal, but first we need to ensure
      // that modal does open when user is redirected back to this listingpage
      callSetInitialValues(setInitialValues, { enquiryModalOpenForListingId: params.id });

      // signup and return back to listingPage.
      history.push(createResourceLocatorString('SignupPage', routeConfiguration(), {}, {}), state);
    } else {
      this.setState({ enquiryModalOpen: true });
    }
  }

  onSubmitEnquiry(values) {
    const { history, params, onSendEnquiry } = this.props;
    const routes = routeConfiguration();
    const listingId = new UUID(params.id);
    const { message } = values;

    onSendEnquiry(listingId, message.trim())
      .then(txId => {
        this.setState({ enquiryModalOpen: false });

        // Redirect to OrderDetailsPage
        history.push(
          createResourceLocatorString('OrderDetailsPage', routes, { id: txId.uuid }, {})
        );
      })
      .catch(() => {
        // Ignore, error handling in duck file
      });
  }

  render() {
    const {
      unitType,
      isAuthenticated,
      currentUser,
      getListing,
      getOwnListing,
      intl,
      onManageDisableScrolling,
      params: rawParams,
      location,
      scrollingDisabled,
      showListingError,
      reviews,
      fetchReviewsError,
      sendEnquiryInProgress,
      sendEnquiryError,
      timeSlots,
      fetchTimeSlotsError,
      filterConfig,
      onFetchTransactionLineItems,
      lineItems,
      fetchLineItemsInProgress,
      fetchLineItemsError,
      viewport,
    } = this.props;

    const listingId = new UUID(rawParams.id);
    const isPendingApprovalVariant = rawParams.variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT;
    const isDraftVariant = rawParams.variant === LISTING_PAGE_DRAFT_VARIANT;
    const currentListing =
      isPendingApprovalVariant || isDraftVariant
        ? ensureOwnListing(getOwnListing(listingId))
        : ensureListing(getListing(listingId));

    const listingSlug = rawParams.slug || createSlug(currentListing.attributes.title || '');
    const params = { slug: listingSlug, ...rawParams };

    const listingType = isDraftVariant
      ? LISTING_PAGE_PARAM_TYPE_DRAFT
      : LISTING_PAGE_PARAM_TYPE_EDIT;
    const listingTab = isDraftVariant ? 'photos' : 'description';

    const isApproved =
      currentListing.id && currentListing.attributes.state !== LISTING_STATE_PENDING_APPROVAL;

    const pendingIsApproved = isPendingApprovalVariant && isApproved;

    // If a /pending-approval URL is shared, the UI requires
    // authentication and attempts to fetch the listing from own
    // listings. This will fail with 403 Forbidden if the author is
    // another user. We use this information to try to fetch the
    // public listing.
    const pendingOtherUsersListing =
      (isPendingApprovalVariant || isDraftVariant) &&
      showListingError &&
      showListingError.status === 403;
    const shouldShowPublicListingPage = pendingIsApproved || pendingOtherUsersListing;

    if (shouldShowPublicListingPage) {
      return <NamedRedirect name="ListingPage" params={params} search={location.search} />;
    }

    const {
      description = '',
      geolocation: exactGeolocation = null,
      price = null,
      title = '',
      publicData,
    } = currentListing.attributes;
    const approximateGeolocation = publicData?.approximateGeolocation;
    const hasApproximateGeolocation =
      approximateGeolocation &&
      typeof approximateGeolocation.lat === 'number' &&
      typeof approximateGeolocation.lng === 'number';
    const legacyPublicMapGeolocation =
      this.state.publicMapLocationLoadedFor === listingId.uuid
        ? this.state.publicMapGeolocation
        : null;
    const geolocation = hasApproximateGeolocation
      ? new LatLng(approximateGeolocation.lat, approximateGeolocation.lng)
      : legacyPublicMapGeolocation
      ? legacyPublicMapGeolocation
      : isPendingApprovalVariant || isDraftVariant
      ? exactGeolocation
      : null;
    const locationAlreadyApproximate =
      hasApproximateGeolocation || this.state.publicMapLocationApproximate;

    const richTitle = (
      <span>
        {richText(title, {
          longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE,
          longWordClass: css.longWord,
        })}
      </span>
    );

    const bookingTitle = (
      <FormattedMessage id="ListingPage.bookingTitle" values={{ title: richTitle }} />
    );
    const bookingSubTitle = intl.formatMessage({ id: 'ListingPage.bookingSubTitle' });

    const topbar = <TopbarContainer />;

    if (showListingError && showListingError.status === 404) {
      // 404 listing not found

      return <NotFoundPage />;
    } else if (showListingError) {
      // Other error in fetching listing

      const errorTitle = intl.formatMessage({
        id: 'ListingPage.errorLoadingListingTitle',
      });

      return (
        <Page title={errorTitle} scrollingDisabled={scrollingDisabled}>
          <LayoutSingleColumn className={css.pageRoot}>
            <LayoutWrapperTopbar>{topbar}</LayoutWrapperTopbar>
            <LayoutWrapperMain>
              <p className={css.errorText}>
                <FormattedMessage id="ListingPage.errorLoadingListingMessage" />
              </p>
            </LayoutWrapperMain>
            <LayoutWrapperFooter>
              <Footer />
            </LayoutWrapperFooter>
          </LayoutSingleColumn>
        </Page>
      );
    } else if (!currentListing.id) {
      // Still loading the listing

      const loadingTitle = intl.formatMessage({
        id: 'ListingPage.loadingListingTitle',
      });

      return (
        <Page title={loadingTitle} scrollingDisabled={scrollingDisabled}>
          <LayoutSingleColumn className={css.pageRoot}>
            <LayoutWrapperTopbar>{topbar}</LayoutWrapperTopbar>
            <LayoutWrapperMain>
              <p className={css.loadingText}>
                <FormattedMessage id="ListingPage.loadingListingMessage" />
              </p>
            </LayoutWrapperMain>
            <LayoutWrapperFooter>
              <Footer />
            </LayoutWrapperFooter>
          </LayoutSingleColumn>
        </Page>
      );
    }

    const handleViewPhotosClick = e => {
      // Stop event from bubbling up to prevent image click handler
      // trying to open the carousel as well.
      e.stopPropagation();
      this.setState({
        imageCarouselOpen: true,
      });
    };
    const authorAvailable = currentListing && currentListing.author;
    const userAndListingAuthorAvailable = !!(currentUser && authorAvailable);
    const isOwnListing =
      userAndListingAuthorAvailable && currentListing.author.id.uuid === currentUser.id.uuid;
    const showContactUser = authorAvailable && (!currentUser || (currentUser && !isOwnListing));

    const currentAuthor = authorAvailable ? currentListing.author : null;
    const ensuredAuthor = ensureUser(currentAuthor);

    // When user is banned or deleted the listing is also deleted.
    // Because listing can be never showed with banned or deleted user we don't have to provide
    // banned or deleted display names for the function
    const authorDisplayName = userDisplayNameAsString(ensuredAuthor, '');
    const requesterDisplayName = userDisplayNameAsString(currentUser, '');
    const displayPrice = getListingCustomerDisplayPrice(currentListing) || price;
    const { formattedPrice } = priceData(displayPrice, intl);

    const handleBookingSubmit = values => {
      const isCurrentlyClosed = currentListing.attributes.state === LISTING_STATE_CLOSED;
      if (isOwnListing || isCurrentlyClosed) {
        window.scrollTo(0, 0);
      } else {
        this.handleSubmit(values);
      }
    };
    const isDesktopBookingLayout = viewport.width >= DESKTOP_BOOKING_PANEL_MIN_WIDTH;
    const bookingPanel = (
      <BookingPanel
        className={css.bookingPanel}
        listing={currentListing}
        isOwnListing={isOwnListing}
        unitType={unitType}
        onSubmit={handleBookingSubmit}
        title={bookingTitle}
        subTitle={bookingSubTitle}
        authorDisplayName={authorDisplayName}
        onManageDisableScrolling={onManageDisableScrolling}
        timeSlots={timeSlots}
        fetchTimeSlotsError={fetchTimeSlotsError}
        onFetchTransactionLineItems={onFetchTransactionLineItems}
        lineItems={lineItems}
        fetchLineItemsInProgress={fetchLineItemsInProgress}
        fetchLineItemsError={fetchLineItemsError}
      />
    );

    const listingImages = (listing, variantName) =>
      (listing.images || [])
        .map(image => {
          const variants = image.attributes.variants;
          const variant = variants ? variants[variantName] : null;

          // deprecated
          // for backwards combatility only
          const sizes = image.attributes.sizes;
          const size = sizes ? sizes.find(i => i.name === variantName) : null;

          return variant || size;
        })
        .filter(variant => variant != null);

    const facebookImages = listingImages(currentListing, 'facebook');
    const twitterImages = listingImages(currentListing, 'twitter');
    const schemaImages = JSON.stringify(facebookImages.map(img => img.url));
    const siteTitle = config.siteTitle;
    const schemaTitle = intl.formatMessage(
      { id: 'ListingPage.schemaTitle' },
      { title, price: formattedPrice, siteTitle }
    );
    const schemaDescription = intl.formatMessage(
      { id: 'ListingPage.schemaDescription' },
      { title, price: formattedPrice }
    );
    // Sharing a space should preview the space itself. Listing image variants are
    // already absolute CDN urls; fall back to the branded card only when the
    // listing has no photo at all.
    const schemaImage =
      facebookImages.length > 0
        ? facebookImages[0].url
        : `${config.canonicalRootURL}${facebookImage}`;

    const hostLink = (
      <NamedLink
        className={css.authorNameLink}
        name="ListingPage"
        params={params}
        to={{ hash: '#host' }}
      >
        {authorDisplayName}
      </NamedLink>
    );

    const amenityOptions = findOptionsForSelectFilter('amenities', filterConfig);
    const categoryOptions = findOptionsForSelectFilter('category', filterConfig);
    const category =
      publicData && publicData.category ? (
        <span>
          {categoryLabel(categoryOptions, publicData.category)}
          <span className={css.separator}>•</span>
        </span>
      ) : null;

    const denominacionOptions = findOptionsForSelectFilter('denominacion', filterConfig);
    const spaceTypeOptions =
      localizeFilters(findOptionsForSelectFilter('space_type', filterConfig), intl) || [];
    const selectedSpaceTypeKey = getSelectedSpaceTypeKey(
      publicData?.denominacion,
      spaceTypeOptions
    );
    const spaceTypeOption = spaceTypeOptions.find(option => option.key === selectedSpaceTypeKey);
    const spaceTypeLabel =
      spaceTypeOption && spaceTypeOption.key !== SPACE_TYPE_ALL_KEY
        ? spaceTypeOption.label
        : null;
    const area_cOptions = findOptionsForSelectFilter('area_c', filterConfig);
    const orientacionOptions = findOptionsForSelectFilter('orientacion', filterConfig);
    const alturaOptions = findOptionsForSelectFilter('altura', filterConfig);
    const actividadOptions = findOptionsForSelectFilter('actividad_c', filterConfig);
    const amenitiesUupoOptions = findOptionsForSelectFilter('amenities_uupo', filterConfig);
    const amenitiesPopOptions = findOptionsForSelectFilter('amenities_pop', filterConfig);
    const categoriaOptions = findOptionsForSelectFilter('categoria', filterConfig);

    return (
      <Page
        title={schemaTitle}
        scrollingDisabled={scrollingDisabled}
        author={authorDisplayName}
        contentType="website"
        description={schemaDescription}
        facebookImages={facebookImages}
        twitterImages={twitterImages}
        schema={{
          '@context': 'http://schema.org',
          '@type': 'ItemPage',
          description: description,
          name: schemaTitle,
          image: [schemaImage],
        }}
      >
        <LayoutSingleColumn className={css.pageRoot}>
          <LayoutWrapperTopbar>{topbar}</LayoutWrapperTopbar>
          <LayoutWrapperMain>
            <div>
              <SectionImages
                title={title}
                listing={currentListing}
                isOwnListing={isOwnListing}
                editParams={{
                  id: listingId.uuid,
                  slug: listingSlug,
                  type: listingType,
                  tab: listingTab,
                }}
                imageCarouselOpen={this.state.imageCarouselOpen}
                onImageCarouselClose={() => this.setState({ imageCarouselOpen: false })}
                handleViewPhotosClick={handleViewPhotosClick}
                onManageDisableScrolling={onManageDisableScrolling}
              />
              <div className={css.contentContainer}>
                <div className={css.mainContent}>
                  <SectionHeading
                    richTitle={richTitle}
                    category={category}
                    hostLink={hostLink}
                    spaceTypeLabel={spaceTypeLabel}
                    showContactUser={showContactUser}
                    onContactUser={this.onContactUser}
                  />
                  <div className={css.infUppo}>
                    <img src={img1} className={css.imageLogo} />
                    <p className={css.textInfo}>
                      <FormattedMessage id="ListingPage.textUupo" />
                    </p>
                    <NamedLink name="SeguroUupoPage" className={css.enlaceUupo}>
                      <FormattedMessage id="ListingPage.enlaceUupo" />
                    </NamedLink>
                  </div>
                  {!isDesktopBookingLayout ? bookingPanel : null}
                  <SectionDescriptionMaybe description={description} />
                  <SectionDenominacionMaybe
                    options={denominacionOptions}
                    publicData={publicData}
                    intl={intl}
                  />
                  <SectionOrientacionMaybe
                    options={orientacionOptions}
                    publicData={publicData}
                    intl={intl}
                  />
                  <SectionAlturaMaybe options={alturaOptions} publicData={publicData} intl={intl} />
                  <SectionFeaturesMaybe
                    options={area_cOptions}
                    publicData={publicData}
                    intl={intl}
                  />
                  <SectionActividadComercialMaybe
                    options={actividadOptions}
                    publicData={publicData}
                    intl={intl}
                  />
                  <SectionAmenitiesUupoMaybe
                    options={amenitiesUupoOptions}
                    publicData={publicData}
                    intl={intl}
                  />
                  <SectionAmenitiesPopMaybe
                    options={amenitiesPopOptions}
                    publicData={publicData}
                    intl={intl}
                  />
                  <SectionCategoriaMaybe
                    options={categoriaOptions}
                    publicData={publicData}
                    intl={intl}
                  />
                  <SectionMedidasMaybe publicData={publicData} />
                  <SectionMapMaybe
                    geolocation={geolocation}
                    publicData={publicData}
                    listingId={currentListing.id}
                    locationAlreadyApproximate={locationAlreadyApproximate}
                  />
                  <SectionReviews reviews={reviews} fetchReviewsError={fetchReviewsError} />
                  <SectionHostMaybe
                    title={title}
                    listing={currentListing}
                    authorDisplayName={authorDisplayName}
                    requesterDisplayName={requesterDisplayName}
                    onContactUser={this.onContactUser}
                    isEnquiryModalOpen={isAuthenticated && this.state.enquiryModalOpen}
                    onCloseEnquiryModal={() => this.setState({ enquiryModalOpen: false })}
                    sendEnquiryError={sendEnquiryError}
                    sendEnquiryInProgress={sendEnquiryInProgress}
                    onSubmitEnquiry={this.onSubmitEnquiry}
                    currentUser={currentUser}
                    onManageDisableScrolling={onManageDisableScrolling}
                  />
                </div>
                {isDesktopBookingLayout ? bookingPanel : null}
              </div>
            </div>
          </LayoutWrapperMain>
          <LayoutWrapperFooter>
            <Footer />
          </LayoutWrapperFooter>
        </LayoutSingleColumn>
      </Page>
    );
  }
}

ListingPageComponent.defaultProps = {
  unitType: config.bookingUnitType,
  currentUser: null,
  enquiryModalOpenForListingId: null,
  showListingError: null,
  reviews: [],
  fetchReviewsError: null,
  timeSlots: null,
  fetchTimeSlotsError: null,
  sendEnquiryError: null,
  filterConfig: config.custom.filters,
  lineItems: null,
  fetchLineItemsError: null,
};

ListingPageComponent.propTypes = {
  // from withRouter
  history: shape({
    push: func.isRequired,
  }).isRequired,
  location: shape({
    search: string,
  }).isRequired,

  unitType: propTypes.bookingUnitType,
  // from injectIntl
  intl: intlShape.isRequired,

  params: shape({
    id: string.isRequired,
    slug: string,
    variant: oneOf([LISTING_PAGE_DRAFT_VARIANT, LISTING_PAGE_PENDING_APPROVAL_VARIANT]),
  }).isRequired,

  isAuthenticated: bool.isRequired,
  currentUser: propTypes.currentUser,
  getListing: func.isRequired,
  getOwnListing: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  scrollingDisabled: bool.isRequired,
  enquiryModalOpenForListingId: string,
  showListingError: propTypes.error,
  callSetInitialValues: func.isRequired,
  reviews: arrayOf(propTypes.review),
  fetchReviewsError: propTypes.error,
  timeSlots: arrayOf(propTypes.timeSlot),
  fetchTimeSlotsError: propTypes.error,
  sendEnquiryInProgress: bool.isRequired,
  sendEnquiryError: propTypes.error,
  onSendEnquiry: func.isRequired,
  onInitializeCardPaymentData: func.isRequired,
  filterConfig: array,
  onFetchTransactionLineItems: func.isRequired,
  lineItems: array,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,
  viewport: shape({
    width: number.isRequired,
    height: number.isRequired,
  }).isRequired,
};

const mapStateToProps = state => {
  const { isAuthenticated } = state.Auth;
  const {
    showListingError,
    reviews,
    fetchReviewsError,
    timeSlots,
    fetchTimeSlotsError,
    sendEnquiryInProgress,
    sendEnquiryError,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    enquiryModalOpenForListingId,
  } = state.ListingPage;
  const { currentUser } = state.user;

  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const getOwnListing = id => {
    const ref = { id, type: 'ownListing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  return {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    scrollingDisabled: isScrollingDisabled(state),
    enquiryModalOpenForListingId,
    showListingError,
    reviews,
    fetchReviewsError,
    timeSlots,
    fetchTimeSlotsError,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    sendEnquiryInProgress,
    sendEnquiryError,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  callSetInitialValues: (setInitialValues, values, saveToSessionStorage) =>
    dispatch(setInitialValues(values, saveToSessionStorage)),
  onFetchTransactionLineItems: (bookingData, listingId, isOwnListing) =>
    dispatch(fetchTransactionLineItems(bookingData, listingId, isOwnListing)),
  onSendEnquiry: (listingId, message) => dispatch(sendEnquiry(listingId, message)),
  onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const ListingPage = compose(
  withRouter,
  withViewport,
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl
)(ListingPageComponent);

export default ListingPage;
